import {
  SURVEY_OTHER_VALUE,
  type Survey,
  type SurveyAnswerValue,
  type SurveyResponse,
} from '@citizen-walk/shared-types';

/**
 * Survey response 出力 helper (GAP-A10、Sprint BE / AW8)。
 *
 * 旧 v1 `old/apps/admin/src/lib/surveyResponses.ts::responsesToCsv` を v2 で踏襲しつつ、
 * client-side で完結する pure 関数として切出 (callable 不要、admin が browser から直接 Blob 出力)。
 *
 * 仕様:
 *   - CSV: UTF-8 BOM 無し (client 側で先頭 0xFEFF を付与する場合あり)、ヘッダー行付き、`\r\n` 区切り。
 *     header = [respondentUid, submittedAt, updatedAt] +
 *              (collectParticipantProfile ? profile fields + joinedSessionIds) + questions[].title
 *   - JSON: `{ survey: {...}, responses: [...] }` の wrap object、UTF-8 文字列。
 *   - 全 question type を文字列化 (旧2 `answerToCsvCell` 踏襲、checkbox は ' / ' 区切り)。
 *
 * AW8 (docs/108) で追加:
 *   - (2026-10-07) 選択肢の回答は文言で保存する方式に一本化したため id→label 解決は廃止
 *   - Low: joinedSessionIds 別列復活 + updatedAt 列 (旧2 buildResponsesCsv 踏襲)
 */

/** CSV cell escape (含む `,` / `"` / 改行を quote 化、内部の " は "" にエスケープ)。 */
export function csvEscape(value: string): string {
  if (value === '') return '';
  if (/[",\r\n]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

/**
 * SurveyAnswerValue を CSV 用文字列に変換。
 *
 * 選択肢の回答は文言で保存されているのでそのまま出す。「その他」(記入欄付き) は記入内容を
 * 括弧で添えて `その他（記入内容）` にする (投稿の入力項目の「その他（記入）」と同じ表記)。
 * 複数選択は ' / ' 区切り。
 */
export function answerValueToString(value: SurveyAnswerValue | undefined): string {
  if (!value) return '';
  switch (value.type) {
    case 'text':
      return value.value;
    case 'choice':
      return withOtherText(value.value, value.otherText);
    case 'choices':
      return value.values.map((v) => withOtherText(v, value.otherText)).join(' / ');
    case 'number':
      return String(value.value);
    case 'date':
    case 'time':
      return value.value;
    default:
      return '';
  }
}

/** 「その他」の値に記入内容を添える。記入が無い / 「その他」以外はそのまま。 */
function withOtherText(label: string, otherText: string | undefined): string {
  const text = otherText?.trim();
  return label === SURVEY_OTHER_VALUE && text ? `${label}（${text}）` : label;
}

/**
 * Survey response 配列を CSV string に変換。
 *
 * 仕様:
 *   - 1 行目: header (respondentUid, submittedAt[, ageGroup, gender, relationship, attribute, joinedSessions], question titles...)
 *   - 2 行目以降: 各 response の値
 *   - profile fields は `survey.collectParticipantProfile === true` のときのみ含める
 *   - submittedAt は ISO8601 のまま (Excel が認識可能)
 *   - joinedSessions は `name(code)` を ' / ' 区切り
 *
 * 旧 v1 の writeResponsesToWorkbook xlsx 出力と異なり、CSV 1 file 完結。
 */
export function responsesToCsv(
  survey: Pick<Survey, 'id' | 'title' | 'questions' | 'collectParticipantProfile'>,
  responses: SurveyResponse[],
  /**
   * uid → 同一人物ID (代表端末 uid) の対応表 (2026-09-18)。端末の引き継ぎで同じ人が複数 uid に
   * なるため、人単位で集計できるよう出力時点で列に入れる。省略時は uid をそのまま入れる。
   */
  personIdByUid?: Map<string, string>,
): string {
  const profileHeaders = survey.collectParticipantProfile
    ? [
        'nickname',
        'ageGroup',
        'gender',
        'relationship',
        'attribute',
        'joinedSessions',
        'joinedSessionIds',
      ]
    : [];
  const questionHeaders = survey.questions.map((q) => q.title || q.id);
  const header = [
    'respondentUid',
    'respondentPersonId',
    'submittedAt',
    'updatedAt',
    ...profileHeaders,
    ...questionHeaders,
  ]
    .map(csvEscape)
    .join(',');

  const rows = responses.map((r) => {
    const sessions = r.participantSnapshot.joinedSessions ?? [];
    const profileCells = survey.collectParticipantProfile
      ? [
          r.participantSnapshot.nickname ?? '',
          r.participantSnapshot.ageGroup ?? '',
          r.participantSnapshot.gender ?? '',
          r.participantSnapshot.relationship ?? '',
          r.participantSnapshot.attribute ?? '',
          sessions.map((s) => `${s.name}(${s.code})`).join(' / '),
          sessions.map((s) => s.sessionId).join(' / '),
        ]
      : [];
    const answerCells = survey.questions.map((q) => answerValueToString(r.answers[q.id]));
    return [
      r.participantUid,
      personIdByUid?.get(r.participantUid) ?? r.participantUid,
      r.createdAt,
      r.updatedAt,
      ...profileCells,
      ...answerCells,
    ]
      .map(csvEscape)
      .join(',');
  });

  return [header, ...rows].join('\r\n');
}

/**
 * Survey response 配列を JSON string に変換。
 * survey の metadata (id/title/questions) を含めて研究者が解析しやすい形にする。
 */
export function responsesToJson(
  survey: Pick<
    Survey,
    'id' | 'title' | 'description' | 'questions' | 'collectParticipantProfile' | 'version'
  >,
  responses: SurveyResponse[],
  personIdByUid?: Map<string, string>,
): string {
  return JSON.stringify(
    {
      survey: {
        id: survey.id,
        title: survey.title,
        description: survey.description,
        version: survey.version,
        collectParticipantProfile: survey.collectParticipantProfile,
        questions: survey.questions,
      },
      responseCount: responses.length,
      exportedAt: new Date().toISOString(),
      // 各回答に同一人物ID を付ける (端末の引き継ぎを畳んだ人単位の集計軸)。
      responses: responses.map((r) => ({
        ...r,
        personId: personIdByUid?.get(r.participantUid) ?? r.participantUid,
      })),
    },
    null,
    2,
  );
}

/** 既定ファイル名 `survey_<surveyId>_<yyyymmdd_hhmm>.<ext>` (旧 v1 と同 pattern)。 */
export function buildResponseFilename(
  surveyId: string,
  ext: 'csv' | 'json',
  now: Date = new Date(),
): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  const stamp =
    `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}` +
    `_${pad(now.getHours())}${pad(now.getMinutes())}`;
  return `survey_${surveyId}_${stamp}.${ext}`;
}
